import { useState } from 'react';

import { faCirclePlay, faCircleStop } from '@fortawesome/pro-regular-svg-icons';

import { Permissions } from '@cloud-wave/neon-common-lib';
import { RINGTONE_OPTIONS, RingtoneNames, TRingtoneNames } from '@cloud-wave/neon-common-lib/app';

import { usePermissionsContext } from 'lib/common/contexts/PermissionsContext';

import Badge from 'lib/common/components/Badge';
import { BadgeTypes } from 'lib/common/components/Badge/Badge';
import ClickableIcon from 'lib/common/components/ClickableIcon/ClickableIcon';
import Label from 'lib/common/components/Label';
import ReactSelect from 'lib/common/components/ReactSelect';
import Text from 'lib/common/components/Text';

interface RingtoneSelectionProps extends RingtoneToggleProps {
  ringtone: string;
  setRingtone: (ringtone: string) => void;
  stopRingtonePlaying: () => void;
}

interface RingtoneToggleProps {
  ringtone: string;
  ringtonePlaying: boolean;
  ringtoneAudio: HTMLAudioElement;
  toggleRingtonePlaying: () => void;
}

const RingtonePreviewToggle = ({
  ringtone,
  ringtonePlaying,
  ringtoneAudio,
  toggleRingtonePlaying,
}: RingtoneToggleProps) => {
  const handleClick = async () => {
    const isPause = !ringtoneAudio.paused;
    const action = isPause ? 'pause' : 'play';

    try {
      await ringtoneAudio[action]();
      toggleRingtonePlaying();
      if (isPause) ringtoneAudio.currentTime = 0;
    } catch {
      /** Silently fail https://goo.gl/LdLk22 */
    }
  };

  return ringtone !== RingtoneNames.NO_RINGTONE ? (
    <ClickableIcon
      aria-label={`${ringtonePlaying ? 'Stop' : 'Play'} ringtone`}
      icon={ringtonePlaying ? faCircleStop : faCirclePlay}
      className="preferences__play-icon"
      onClick={handleClick}
    />
  ) : null;
};

type RingtoneOption = {
  value: string;
  label: string;
  description: string;
  ringtoneName: TRingtoneNames;
};

export default function RingtonePreference({
  setRingtone,
  stopRingtonePlaying,
  toggleRingtonePlaying,
  ringtoneAudio,
  ringtonePlaying,
  ringtone,
}: RingtoneSelectionProps) {
  const ringtoneList: RingtoneOption[] = Object.entries(RINGTONE_OPTIONS).map(([key, value]) => ({
    ...value,
    ringtoneName: key as TRingtoneNames,
  }));

  const [ringtoneChanged, setRingtoneChanged] = useState(false);

  const { hasPermission } = usePermissionsContext();
  const hasRestrictedRingtonePermission = hasPermission({
    permission: Permissions.RESTRICTED_RINGTONE,
  });

  if (hasRestrictedRingtonePermission) {
    return (
      <div className="mt-15" data-testid="restricted-ringtone">
        <div className="next-to-10">
          <Text type="heading3">Ringtone</Text>
          <Badge label="Administrator Managed" type={BadgeTypes.NEUTRAL} />
        </div>
        <div className="next-to-10 mt-10">
          <Text type="body" color="black">
            {RINGTONE_OPTIONS[ringtone].label}
          </Text>
          <RingtonePreviewToggle
            ringtone={ringtone}
            ringtonePlaying={ringtonePlaying}
            ringtoneAudio={ringtoneAudio}
            toggleRingtonePlaying={toggleRingtonePlaying}
          />
        </div>
      </div>
    );
  }

  return (
    <Label text="Call / Chat Ringtone" id="Ringtone" className="mt-10">
      <div style={{ display: 'contents' }}>
        <div className="preferences__ringtone">
          <ReactSelect
            aria-label="Call / Chat Ringtone"
            classNamePrefix="react-select"
            isSearchable={false}
            id="preference-ringtone"
            options={ringtoneList}
            value={ringtoneList.find((ringtoneOption) => ringtoneOption.value === RINGTONE_OPTIONS[ringtone].value)}
            onChange={(ringtoneSelection: RingtoneOption) => {
              if (!ringtoneSelection) {
                return;
              }

              setRingtoneChanged(true);
              setRingtone(ringtoneSelection.ringtoneName);
              stopRingtonePlaying();

              ringtoneAudio.src = ringtoneSelection.value;
            }}
          />
          <RingtonePreviewToggle
            ringtone={ringtone}
            ringtonePlaying={ringtonePlaying}
            ringtoneAudio={ringtoneAudio}
            toggleRingtonePlaying={toggleRingtonePlaying}
          />
        </div>
        {ringtoneChanged && (
          <div className="preferences__warning-text">
            Please reload the page for the ringtone changes to take effect.
          </div>
        )}
      </div>
    </Label>
  );
}
